import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, Plus, Terminal } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Page } from "@/components/layout/page";
import { ActionBar } from "@/components/layout/action-bar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/empty-state";
import { EntityErrorState } from "@/components/layout/entity-state";
import { FilteredEmptyState } from "@/components/ui/filtered-empty-state";
import { GenerateScenarioModal } from "@/components/experience/generate-scenario-modal";
import { useCanEdit, useEditorCopy } from "@/lib/project-access";
import type { OutlineListItem, ScenarioSortKey } from "./scenarios-tab-types";
import { ScenariosToolbar } from "./scenarios-toolbar";
import { ScenarioGroup } from "./scenario-group";

interface ScenariosTabProps {
  projectId: string;
}

/**
 * ScenariosTab — the primary project-scoped index. Groups scenarios by
 * capability, shows persona verdict chips per cast persona, links to
 * scenario detail (situation + cast view).
 */
export function ScenariosTab({ projectId }: ScenariosTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get("search") ?? "";
  const sortParam = searchParams.get("sort");
  const sortKey: ScenarioSortKey =
    sortParam === "newest" ||
    sortParam === "oldest" ||
    sortParam === "recent_activity"
      ? sortParam
      : "recent_activity";

  const handleSearchChange = (val: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (val.trim()) {
          next.set("search", val);
        } else {
          next.delete("search");
        }
        return next;
      },
      { replace: true },
    );
  };

  const handleSortChange = (val: ScenarioSortKey) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (val !== "recent_activity") {
          next.set("sort", val);
        } else {
          next.delete("sort");
        }
        return next;
      },
      { replace: true },
    );
  };

  const [newScenarioMode, setNewScenarioMode] = useState<
    "manual" | "agent" | null
  >(null);
  const canEdit = useCanEdit();
  const emptyDescription = useEditorCopy(
    "Scenarios define user situations and evaluation questions in domain language to evaluate your product.",
    "Scenarios define user situations and evaluation questions in domain language. Ask an editor to add the first one.",
  );

  const { data, isLoading, error, refetch } = trpc.outline.list.useQuery({
    projectId,
  });

  const { data: capabilitiesList } = trpc.capability.list.useQuery({
    projectId,
  });

  const scenarios = useMemo(() => {
    const arr = ((data as OutlineListItem[] | undefined) ?? []).slice();
    switch (sortKey) {
      case "oldest":
        arr.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
      case "recent_activity":
        arr.sort((a, b) => {
          const dateA = a.lastWalkthroughAt ?? a.createdAt;
          const dateB = b.lastWalkthroughAt ?? b.createdAt;
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        });
        break;
      default:
        arr.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
    }
    return arr;
  }, [data, sortKey]);

  const filteredScenarios = useMemo(() => {
    if (!scenarios) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return scenarios;
    return scenarios.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q) ||
        s.tension?.toLowerCase().includes(q),
    );
  }, [scenarios, searchQuery]);

  const actions = canEdit ? (
    <ActionBar
      primary={
        <Button onClick={() => setNewScenarioMode("manual")}>
          <Plus className="mr-2 h-4 w-4" />
          New scenario
        </Button>
      }
      secondary={[
        <Button
          key="agent"
          variant="outline"
          onClick={() => setNewScenarioMode("agent")}
        >
          <Terminal className="mr-2 h-4 w-4" />
          Draft with agent
        </Button>,
      ]}
    />
  ) : undefined;

  if (isLoading) {
    return (
      <Page title="Scenarios" actions={actions}>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Scenarios" actions={actions}>
        <EntityErrorState
          title="Failed to load scenarios"
          description="We couldn't retrieve the scenarios for this project. Please try again."
          onRetry={() => refetch()}
          embedded
        />
      </Page>
    );
  }

  const capabilities = capabilitiesList ?? [];
  const grouped = new Map<
    string,
    { name: string; scenarios: typeof scenarios }
  >();
  const ungrouped: typeof scenarios = [];

  for (const o of filteredScenarios) {
    const capId = o.primaryCapabilityId ?? null;
    if (capId) {
      if (!grouped.has(capId)) {
        const cap = capabilities.find((c) => c.id === capId);
        grouped.set(capId, {
          name: cap?.name ?? "Unknown",
          scenarios: [],
        });
      }
      grouped.get(capId)!.scenarios.push(o);
    } else {
      ungrouped.push(o);
    }
  }

  const hasActiveFilters = searchQuery.trim().length > 0;
  const resetFilters = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("search");
        return next;
      },
      { replace: true },
    );
  };

  return (
    <Page title="Scenarios" actions={actions}>
      {scenarios.length > 0 && (
        <ScenariosToolbar
          projectId={projectId}
          scenarioCount={scenarios.length}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          sortKey={sortKey}
          onSortChange={handleSortChange}
          hasActiveFilters={hasActiveFilters}
          onResetFilters={resetFilters}
        />
      )}

      {scenarios.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No scenarios yet"
          description={emptyDescription}
          actions={
            canEdit ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setNewScenarioMode("agent")}
                >
                  <Terminal className="mr-2 h-4 w-4" />
                  Draft with agent
                </Button>
                <Button onClick={() => setNewScenarioMode("manual")}>
                  <Plus className="mr-2 h-4 w-4" />
                  New scenario
                </Button>
              </>
            ) : undefined
          }
          variant="compact"
        />
      ) : filteredScenarios.length === 0 ? (
        <FilteredEmptyState
          title="No scenarios match these filters"
          description="No scenarios match your search query."
          onReset={resetFilters}
        />
      ) : (
        <div className="space-y-10">
          {[...grouped.entries()].map(([capId, group]) => (
            <ScenarioGroup
              key={capId}
              title={group.name}
              count={group.scenarios.length}
              scenarios={group.scenarios}
              projectId={projectId}
            />
          ))}
          {ungrouped.length > 0 && (
            <ScenarioGroup
              title={grouped.size > 0 ? "Ungrouped" : ""}
              count={ungrouped.length}
              scenarios={ungrouped}
              projectId={projectId}
            />
          )}
        </div>
      )}

      {canEdit && newScenarioMode && (
        <GenerateScenarioModal
          projectId={projectId}
          initialMode={newScenarioMode}
          onClose={() => setNewScenarioMode(null)}
        />
      )}
    </Page>
  );
}
